// src/components/StockTicker.js
import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../utils/api';
import './StockTicker.css';

const StockTicker = () => {
    const [tickerItems, setTickerItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedStock, setSelectedStock] = useState(null);
    const [isPaused, setIsPaused] = useState(false); // State to pause auto-scroll
    
    const scrollRef = useRef(null); // Reference to the scroll container

    // 1. Fetch Data
    useEffect(() => {
        const fetchStockData = async () => {
            try {
                const response = await apiClient.get('/nifty-movers');
                
                let gainers = [];
                let losers = [];

                if (response.data.gainers && response.data.losers) {
                    gainers = response.data.gainers;
                    losers = response.data.losers;
                } else if (Array.isArray(response.data)) {
                    const all = response.data.sort((a,b) => parseFloat(b.change) - parseFloat(a.change));
                    gainers = all.slice(0, 10);
                    losers = all.slice(-10).reverse();
                }

                const displayList = [
                    { type: 'header', label: 'TOP GAINERS 🚀', class: 'header-gain' },
                    ...gainers,
                    { type: 'header', label: 'TOP LOSERS 📉', class: 'header-loss' },
                    ...losers
                ];

                setTickerItems(displayList);

            } catch (error) {
                console.error("Error fetching stocks:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchStockData();
        const interval = setInterval(fetchStockData, 1800000); 
        return () => clearInterval(interval);
    }, []);

    // 2. Auto-Scroll Logic (Replaces CSS Animation)
    useEffect(() => {
        const scrollContainer = scrollRef.current;
        let animationFrameId;

        const scroll = () => {
            if (scrollContainer && !isPaused) {
                // Move 1 pixel to the right
                scrollContainer.scrollLeft += 1;

                // Infinite Loop Logic:
                // If we have scrolled past half the content (the end of the first set),
                // Reset instantly to 0 to create the illusion of infinite scrolling.
                // Note: We need a slight buffer (-1) to prevent jitter.
                if (scrollContainer.scrollLeft >= (scrollContainer.scrollWidth / 2)) {
                    scrollContainer.scrollLeft = 0;
                }
            }
            animationFrameId = requestAnimationFrame(scroll);
        };

        // Start the loop
        animationFrameId = requestAnimationFrame(scroll);

        return () => cancelAnimationFrame(animationFrameId);
    }, [isPaused, tickerItems]); // Re-run if paused state changes or items load

    // Helper for formatting
    const formatNumber = (num) => {
        if (!num) return "-";
        if (num >= 1.0e+7) return (num / 1.0e+7).toFixed(2) + " Cr";
        if (num >= 1.0e+5) return (num / 1.0e+5).toFixed(2) + " L";
        return num.toLocaleString();
    };

    if (loading || tickerItems.length === 0) return null;

    return (
        <>
            <div className="stock-ticker-container">
                <div className="ticker-label">MARKET</div>
                
                {/* 
                    3. Scroll Wrapper 
                    - We attach the ref here
                    - We handle mouse/touch events to pause scrolling
                */}
                <div 
                    className="ticker-wrapper" 
                    ref={scrollRef}
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                    onTouchStart={() => setIsPaused(true)}
                    onTouchEnd={() => setIsPaused(false)}
                >
                    <div className="ticker-track">
                        {/* Duplicate the list to allow for infinite scrolling effect */}
                        {[...tickerItems, ...tickerItems].map((item, index) => {
                            if (item.type === 'header') {
                                return (
                                    <div key={`header-${index}`} className={`ticker-header ${item.class}`}>
                                        {item.label}
                                    </div>
                                );
                            }
                            return (
                                <div 
                                    key={`${item.symbol}-${index}`} 
                                    className={`ticker-item ${item.type}`}
                                    onClick={() => setSelectedStock(item)}
                                >
                                    <span className="stock-symbol">{item.symbol}</span>
                                    <span className="stock-price">₹{item.price}</span>
                                    <span className="stock-change">
                                        {item.type === 'gain' ? '▲' : '▼'} {item.change}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* MODAL (Same as before) */}
            {selectedStock && (
                <div className="stock-modal-overlay" onClick={() => setSelectedStock(null)}>
                    <div className="stock-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div>
                                <h2>{selectedStock.symbol}</h2>
                                <p className="modal-subtitle">Nifty 50 Index</p>
                            </div>
                            <button className="close-btn" onClick={() => setSelectedStock(null)}>×</button>
                        </div>
                        <div className="modal-price-section">
                            <span className="big-price">₹{selectedStock.price}</span>
                            <span className={`big-change ${selectedStock.type}`}>
                                {selectedStock.changeValue} ({selectedStock.change})
                            </span>
                        </div>
                        <div className="stock-grid">
                            <div className="grid-item"><span className="label">Day High</span><span className="value">₹{selectedStock.dayHigh}</span></div>
                            <div className="grid-item"><span className="label">Day Low</span><span className="value">₹{selectedStock.dayLow}</span></div>
                            <div className="grid-item"><span className="label">52W High</span><span className="value">₹{selectedStock.fiftyTwoWeekHigh}</span></div>
                            <div className="grid-item"><span className="label">52W Low</span><span className="value">₹{selectedStock.fiftyTwoWeekLow}</span></div>
                            <div className="grid-item"><span className="label">Volume</span><span className="value">{formatNumber(selectedStock.volume)}</span></div>
                            <div className="grid-item"><span className="label">Market Cap</span><span className="value">{formatNumber(selectedStock.marketCap)}</span></div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StockTicker;